/**
 * A card buyer reaches /success from Stripe's `onComplete`, which can run before the
 * `checkout.session.completed` webhook marks the order paid. The page re-reads the order (a
 * database read, no provider call) every 2 s for up to 20 s before it gives up and asks the
 * server to check with the provider once and tell support.
 */
export const SUCCESS_POLL_INTERVAL_MS = 2_000;
export const SUCCESS_POLL_MAX_WAIT_MS = 20_000;

export interface OrderPollResult<T> {
  /** The last order read, or null when every read failed. */
  order: T | null;
  reads: number;
}

/**
 * Reads until the order leaves `pending` (paid, or failed/expired/canceled) or the wait runs out.
 * A failed read counts as "not yet" and is retried. `onWaiting` runs once, the first time the
 * page has to wait, so it can switch to the "preparing your access" screen.
 */
export async function pollOrderUntilSettled<T extends { status: string }>(args: {
  read: () => Promise<T>;
  sleep: (ms: number) => Promise<void>;
  onWaiting?: () => void;
  intervalMs?: number;
  maxWaitMs?: number;
}): Promise<OrderPollResult<T>> {
  const intervalMs = args.intervalMs ?? SUCCESS_POLL_INTERVAL_MS;
  const maxReads = 1 + Math.floor((args.maxWaitMs ?? SUCCESS_POLL_MAX_WAIT_MS) / intervalMs);
  let order: T | null = null;
  let reads = 0;
  while (reads < maxReads) {
    try {
      order = await args.read();
    } catch {
      /* keep the last good read; a network blip is not a verdict */
    }
    reads += 1;
    if (order && order.status !== "pending") break;
    if (reads >= maxReads) break;
    if (reads === 1) args.onWaiting?.();
    await args.sleep(intervalMs);
  }
  return { order, reads };
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
